import type {
  EnvironmentId,
  ProjectId,
  PullRequestReviewPosition,
  SourceControlProviderKind,
} from "@cmmd-forge/contracts";

export interface PullRequestAgentSelection {
  readonly path: string;
  readonly oldPath: string | null;
  readonly start: number;
  readonly end: number;
  readonly position: PullRequestReviewPosition;
  readonly patch: string;
}

/** Narrows a provider patch to the selected file before the handoff applies its byte bound. */
export function pullRequestFilePatch(patch: string, path: string): string {
  const blocks = patch.split(/(?=^diff --git )/mu);
  const selected = blocks.find((block) => patchBlockNamesPath(block, path));
  return selected ?? patch;
}

function patchBlockNamesPath(block: string, path: string) {
  const escapedPath = path.replaceAll("\\", "\\\\").replaceAll('"', '\\"');
  return block.includes(`+++ b/${path}`) || block.includes(`+++ "b/${escapedPath}"`);
}

const MAX_SELECTION_CHARS = 16_384;
const MAX_REQUEST_CHARS = 4_000;

export interface PullRequestSelectionContext {
  readonly environmentId: EnvironmentId;
  readonly projectId: ProjectId;
  readonly provider: SourceControlProviderKind;
  readonly host: string;
  readonly repository: string;
  readonly number: number;
  readonly revision: string;
  readonly path: string;
  readonly startLine: number;
  readonly endLine: number;
  readonly patch: string;
  readonly truncated: boolean;
}

function bounded(
  value: string,
  maximum: number,
): { readonly value: string; readonly truncated: boolean } {
  if (value.length <= maximum) return { value, truncated: false };
  return { value: value.slice(0, maximum), truncated: true };
}

function escapeAttribute(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll('"', "&quot;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;");
}

function neutralizeReviewTags(value: string): string {
  return value.replace(/<(?=\/?review_comment\b)/giu, "&lt;");
}

/** Builds editable, bounded context for a composer; callers must never auto-send it. */
export function buildPullRequestSelectionHandoff(input: {
  readonly context: Omit<PullRequestSelectionContext, "patch" | "truncated">;
  readonly patch: string;
  readonly request: string;
}): { readonly context: PullRequestSelectionContext; readonly prompt: string } {
  const patch = bounded(input.patch, MAX_SELECTION_CHARS);
  const request = bounded(input.request.trim(), MAX_REQUEST_CHARS);
  const context = { ...input.context, patch: patch.value, truncated: patch.truncated };
  const range =
    context.startLine === context.endLine
      ? `L${context.startLine}`
      : `L${context.startLine}-L${context.endLine}`;
  const block = [
    `<review_comment provider="${escapeAttribute(context.provider)}" host="${escapeAttribute(context.host)}" repository="${escapeAttribute(context.repository)}" number="${context.number}" revision="${escapeAttribute(context.revision)}" filePath="${escapeAttribute(context.path)}" range="${range}" truncated="${context.truncated}">`,
    neutralizeReviewTags(request.value),
    "```diff",
    neutralizeReviewTags(context.patch).trimEnd(),
    "```",
    "</review_comment>",
  ].join("\n");
  return { context, prompt: block };
}
